import { Fragment } from "react";
import { DefinitionDescription, DefinitionList, DefinitionTerm } from "@/components/ui/definition-list.tsx";
import Icon from "@/components/ui/icon.tsx";
import { TabsTrigger } from "@/components/ui/tabs.tsx";
import AccessoryDetails from "@/features/sub-families/components/summary/accessory-details.tsx";
import LightDetails from "@/features/sub-families/components/summary/light-details.tsx";
import MountingKitDetails from "@/features/sub-families/components/summary/mounting-kit-details.tsx";
import useConfigCode from "@/features/sub-families/hooks/use-config-code.ts";
import useCurrentProduct from "@/services/product/hooks/use-current-product.ts";
import { type Nullable, SmartNumberDescriptor } from "@/types";
import { useSubFamiliesConfig } from "../../providers/sub-families-config-provider.tsx";
import { ConfigurationTab, type ConfigurationTabEnum } from "../../types";
import FamilyDetails from "./family-details.tsx";
import PartDetails from "./part-details.tsx";

type Props = { tab: ConfigurationTabEnum };
export default function SummaryDetails({ tab }: Props) {
    const { filterOptions } = useSubFamiliesConfig();
    const { getConfigCodeByDescriptor, getEmptyConfigCodeByDescriptor } = useConfigCode();

    const currentProduct = useCurrentProduct();

    const options = Object.values(filterOptions ?? {})
        .map<Nullable<{ label: string; value: string }>>((config) => {
            if (config.tab !== tab) return null; // This option should not be shown here

            const configCode = getConfigCodeByDescriptor(config.descriptor);
            if (!configCode) return null;

            const choice = Object.values(config.choices).find((option) => option.configCode === configCode);
            if (!choice) return null;

            return { label: config.label, value: choice.label };
        })
        .filter((option) => option !== null);

    const accessory = getConfigCodeByDescriptor(SmartNumberDescriptor.Accessory);
    const emptyAccessory = getEmptyConfigCodeByDescriptor(SmartNumberDescriptor.Accessory);

    const isDesign = tab === ConfigurationTab.Design;
    const isLight = tab === ConfigurationTab.Light;
    const isAccessory = tab === ConfigurationTab.Accessory;

    const allAccessories = currentProduct?.properties?.light_acc ?? [];

    // Accessory is the only tab that may not have any options available
    if (isAccessory && (allAccessories.length === 0 || accessory === emptyAccessory)) return null;

    return (
        <div className="summary-details">
            <div className="summary-details__header">
                <h3 className="summary-details__title h4">{tab}</h3>
                <TabsTrigger tab={tab} variant="icon" className="summary-details__edit">
                    <Icon name="edit" />
                </TabsTrigger>
            </div>
            <DefinitionList>
                {isDesign && (
                    <>
                        <FamilyDetails />
                        <MountingKitDetails />
                    </>
                )}
                {options.map(({ label, value }) => (
                    <Fragment key={`${label}-${value}`}>
                        <DefinitionTerm>{label}</DefinitionTerm>
                        <DefinitionDescription>{value}</DefinitionDescription>
                    </Fragment>
                ))}
                {isLight && <LightDetails />}
                {isDesign && <PartDetails />}
                {isAccessory && <AccessoryDetails />}
            </DefinitionList>
        </div>
    );
}
